import * as THREE from "three";
import { Cell, CellKind, Grid } from "@highmarch/domain";
import { CRITTER_SHADOW_ALPHA, Critter, CritterKind, CritterViews, CritterWorld, critterGround, softDisc } from "./critters";
import { Butterflies, Meadow } from "./meadow";

/**
 * The hub's wildlife (R7, P1.12.44): a few rabbits and a bird on the lawn round the fortress and its stands, and
 * butterflies over it — the level's critters (critters.ts) with nothing to flee from. They nibble, sit up, look
 * round and hop from cell to cell on the drawn ground (GroundMesh.drawnAt), never onto the fortress, a stand's pad
 * or out of the framed shot. Presentation only; the hub renders them at its ambient rate (hubRender.ts).
 */
export class HubCritters implements CritterWorld {
  readonly group = new THREE.Group();
  private readonly critters: Critter[] = [];
  private readonly cells: Cell[];
  private readonly shadowMat: THREE.MeshBasicMaterial;
  private readonly shadowGeo = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
  private readonly butterflies: Butterflies;
  /** How many live on the lawn: enough to notice, few enough that the line-up stays the subject. */
  static readonly Count = 3;
  /** Of them, rabbits (the rest are birds): a rabbit's hop reads best at the hub's distance. */
  static readonly Rabbits = 2;
  static readonly ButterflyCount = 3;
  /** Cells round the fortress footprint kept clear (its walls overhang the footprint; the stands' pads start here). */
  static readonly FortressClear = 1;
  /**
   * The lawn, as offsets from the fortress cell at the hub camera's rest yaw (HubLayout.Stands): `(dx + dz)` is depth
   * (negative is toward the camera), `(dz − dx)` is across. Cells behind the keep are hidden by it, and past these
   * bounds a critter would stand outside the framed band or in the haze.
   */
  static readonly Depth = [-5, 1] as const;
  static readonly Across = 5;

  constructor(grid: Grid, base: Cell, stands: readonly Cell[]) {
    this.cells = HubCritters.lawn(grid, base, stands);
    this.shadowMat = new THREE.MeshBasicMaterial({ map: softDisc(), transparent: true, opacity: CRITTER_SHADOW_ALPHA, depthWrite: false, toneMapped: false });
    for (let i = 0; i < HubCritters.Count; i++) this.spawn(i < HubCritters.Rabbits ? "rabbit" : "bird");
    this.butterflies = new Butterflies(Meadow.reduced() ? 1 : HubCritters.ButterflyCount, () => this.flutterSpot());
    this.group.add(this.butterflies.mesh);
  }

  /** The cells a critter may stand on: free lawn in the framed band, off the fortress's ring and the stands. */
  static lawn(grid: Grid, base: Cell, stands: readonly Cell[]): Cell[] {
    const out: Cell[] = [];
    const r = HubCritters.FortressClear;
    for (let y = 0; y < grid.height; y++) for (let x = 0; x < grid.width; x++) {
      const c = new Cell(x, y), dx = x - base.x, dz = y - base.y;
      if (dx + dz < HubCritters.Depth[0] || dx + dz > HubCritters.Depth[1] || Math.abs(dz - dx) > HubCritters.Across) continue;
      if (grid.kindAt(c) !== CellKind.Empty || stands.some((s) => s.equals(c))) continue;
      let nearKeep = false;
      for (let oy = -r; oy <= r && !nearKeep; oy++) for (let ox = -r; ox <= r; ox++) {
        const n = new Cell(x + ox, y + oy);
        if (grid.inBounds(n) && grid.kindAt(n) === CellKind.Base) { nearKeep = true; break; }
      }
      if (!nearKeep) out.push(c);
    }
    return out;
  }

  update(dt: number): void {
    for (const c of this.critters) c.update(dt, [], false, this);
    this.butterflies.update(dt, false);
  }

  nextSpot(from: Cell): { cell: Cell; at: THREE.Vector3 } | null {
    const near = this.cells.filter((c) => !c.equals(from) && Math.max(Math.abs(c.x - from.x), Math.abs(c.y - from.y)) <= 1 && !this.critters.some((k) => k.cell.equals(c)));
    if (near.length === 0) return null;
    const cell = near[Math.floor(Math.random() * near.length)];
    const x = cell.x + (Math.random() - 0.5) * CritterViews.CellJitter, z = cell.y + (Math.random() - 0.5) * CritterViews.CellJitter;
    return { cell, at: new THREE.Vector3(x, critterGround(x, z), z) };
  }

  private spawn(kind: CritterKind): void {
    const free = this.cells.filter((c) => !this.critters.some((k) => k.cell.equals(c)));
    if (free.length === 0) return;
    const c = new Critter(kind, free[Math.floor(Math.random() * free.length)], this.shadowMat, this.shadowGeo);
    this.critters.push(c);
    this.group.add(c.root, c.shadow);
  }

  private flutterSpot(): THREE.Vector3 | null {
    if (this.cells.length === 0) return null;
    const c = this.cells[Math.floor(Math.random() * this.cells.length)];
    return new THREE.Vector3(c.x + (Math.random() - 0.5) * 0.8, 0, c.y + (Math.random() - 0.5) * 0.8);
  }

  dispose(): void {
    for (const c of this.critters) { this.group.remove(c.root, c.shadow); c.dispose(); }
    this.critters.length = 0;
    this.shadowMat.map?.dispose(); this.shadowMat.dispose(); this.shadowGeo.dispose();
    this.butterflies.dispose();
  }
}
